import { useState, useEffect } from 'react';
import { ArrowLeftRight, Plus, X, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { useIsAuthenticated } from '@azure/msal-react';
import { haalRuilverzoeken, trekRuilverzoekInApi, weigerRuilverzoekApi, beoordeelRuilverzoekApi, verwijderRuilHistorieApi, wisRuilHistorieApi } from '../lib/api.js';
import NieuwRuilverzoekModal from '../components/NieuwRuilverzoekModal.jsx';
import TegendienstModal from '../components/TegendienstModal.jsx';

const DAG_LABEL = {
  ma: 'maandag', di: 'dinsdag', wo: 'woensdag', do: 'donderdag',
  vr: 'vrijdag', za: 'zaterdag', zo: 'zondag',
};

// Badge-kleuren (tailwind bg-*-100 / text-*-700) per logische status-kleur.
const BADGE_KLEUR = {
  groen: 'bg-green-100 text-green-700',
  oranje: 'bg-orange-100 text-orange-700',
  rood: 'bg-red-100 text-red-700',
  grijs: 'bg-slate-100 text-slate-600',
  blauw: 'bg-blue-100 text-blue-700',
};

// Sobere, gecentreerde melding in de bestaande kaart-stijl (zoals TeamRooster).
function Melding({ emoji, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
      <div className="text-4xl mb-3">{emoji}</div>
      {children}
    </div>
  );
}

// Eén dienst in gewone taal: "maandag 2026-09-07 — Late dienst Arnhem".
function dienstTekst(d) {
  if (!d) return '';
  const wanneer = [DAG_LABEL[d.dag] || d.dag, d.datum].filter(Boolean).join(' ');
  const wat = [d.dienstLabel, d.gemeenteLabel].filter(Boolean).join(' ');
  return wat ? `${wanneer} — ${wat}` : wanneer;
}

// Status → begrijpelijke tekst + kleur, ROLBEWUST (indiener vs. collega).
function statusInfo(v, mwKey) {
  const benIndiener = v.indienerKey === mwKey;
  switch (v.status) {
    case 'voorgesteld':
      return benIndiener
        ? { tekst: `Wacht op reactie van ${v.collegaNaam}`, kleur: 'blauw' }
        : { tekst: 'Jij moet nog reageren', kleur: 'oranje' };
    case 'tegendienst_gekozen':
      return { tekst: 'Wacht op goedkeuring teamleider', kleur: 'oranje' };
    case 'goedgekeurd':
      return { tekst: 'Goedgekeurd — de ruil is rond', kleur: 'groen' };
    case 'afgewezen':
      return { tekst: 'Afgewezen door teamleider', kleur: 'rood' };
    case 'geweigerd':
      return benIndiener
        ? { tekst: `${v.collegaNaam} heeft geweigerd`, kleur: 'grijs' }
        : { tekst: 'Je hebt geweigerd', kleur: 'grijs' };
    case 'ingetrokken':
      return { tekst: 'Ingetrokken', kleur: 'grijs' };
    default:
      return { tekst: v.status, kleur: 'grijs' };
  }
}

// Kaart voor "Mijn ruilverzoeken": beide diensten + rolbewuste status-badge.
// Indiener: kan intrekken zolang niet beoordeeld. Gevraagde collega: kan bij 'voorgesteld'
// accepteren (tegendienst kiezen) of weigeren.
function VerzoekKaart({ v, mwKey, onIntrek, onWeiger, onAccepteer, onVerwijder }) {
  const info = statusInfo(v, mwKey);
  const kanIntrekken =
    v.indienerKey === mwKey && (v.status === 'voorgesteld' || v.status === 'tegendienst_gekozen');
  const kanReageren = v.collegaKey === mwKey && v.status === 'voorgesteld';
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1 text-sm">
          <div className="text-slate-700">
            <span className="font-medium">{v.indienerNaam}</span> biedt: {dienstTekst(v.aangebodenDienst)}
          </div>
          {v.tegendienst ? (
            <div className="text-slate-700">
              <span className="font-medium">{v.collegaNaam}</span> biedt terug: {dienstTekst(v.tegendienst)}
            </div>
          ) : (
            <div className="text-slate-400 italic">wacht op tegendienst van {v.collegaNaam}</div>
          )}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${BADGE_KLEUR[info.kleur]}`}>
              {info.tekst}
            </span>
            {onVerwijder && (
              <button
                onClick={onVerwijder}
                title="Uit historie verwijderen"
                className="text-slate-400 hover:text-slate-600 shrink-0"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {kanIntrekken && (
            <button
              onClick={() => onIntrek(v.id)}
              className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-600 transition-colors"
            >
              <X className="w-3.5 h-3.5" /> Intrekken
            </button>
          )}
          {kanReageren && (
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => onAccepteer(v)}
                className="inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:text-green-800 transition-colors"
              >
                <Check className="w-3.5 h-3.5" /> Accepteren
              </button>
              <button
                onClick={() => onWeiger(v.id)}
                className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-600 transition-colors"
              >
                <X className="w-3.5 h-3.5" /> Weigeren
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Kaart voor "Te beoordelen" (teamleider): het complete paar + goedkeuren/afwijzen-knoppen.
// Beoordelen loopt via de server (bij goedkeuren de dienst-wissel in de momentopname). Een
// serverfout (bijv. "week niet meer vastgelegd" / "rooster gewijzigd", 409) tonen we zichtbaar
// in het rood bij de kaart, zodat de teamleider weet WAAROM een goedkeuring niet lukte.
function BeoordeelKaart({ v, onBeoordeeld }) {
  const [fout, setFout] = useState(null);
  const [bezig, setBezig] = useState(false);

  const beoordeel = async (akkoord) => {
    if (!window.confirm(akkoord ? 'Deze ruil goedkeuren? Het rooster wordt aangepast.' : 'Deze ruil afwijzen?')) return;
    setFout(null);
    setBezig(true);
    try {
      await beoordeelRuilverzoekApi(v.id, akkoord);
      onBeoordeeld();
    } catch (err) {
      setFout(err.message);
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 text-sm space-y-2">
      <div className="space-y-1">
        <div className="text-slate-700">
          <span className="font-medium">{v.indienerNaam}</span> biedt: {dienstTekst(v.aangebodenDienst)}
        </div>
        <div className="text-slate-700">
          <span className="font-medium">{v.collegaNaam}</span> biedt terug: {dienstTekst(v.tegendienst)}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button
          onClick={() => beoordeel(true)}
          disabled={bezig}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
        >
          <Check className="w-3.5 h-3.5" /> Goedkeuren
        </button>
        <button
          onClick={() => beoordeel(false)}
          disabled={bezig}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 bg-slate-100 hover:bg-red-100 hover:text-red-700 disabled:opacity-50"
        >
          <X className="w-3.5 h-3.5" /> Afwijzen
        </button>
      </div>
      {fout && <div className="text-xs text-red-600">{fout}</div>}
    </div>
  );
}

export default function Ruilverzoeken({ previewAlsMedewerker = false }) {
  const isAuthenticated = useIsAuthenticated();
  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(false);
  const [fout, setFout] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [herlaad, setHerlaad] = useState(0);
  const [tegendienstVerzoek, setTegendienstVerzoek] = useState(null);
  const [historieOpen, setHistorieOpen] = useState(false);

  // Haal de verzoeken bij login én opnieuw na het aanmaken van een verzoek: de modal bumpt
  // via onAangemaakt de herlaad-teller, wat dit effect opnieuw laat draaien. Geen maand-
  // navigatie: het endpoint gebruikt de token-identiteit en geeft álle verzoeken.
  useEffect(() => {
    if (!isAuthenticated) return;
    let geannuleerd = false;

    const laad = async () => {
      setLaden(true);
      setFout(null);
      try {
        const res = await haalRuilverzoeken();
        if (!geannuleerd) setData(res);
      } catch (err) {
        if (!geannuleerd) setFout(err.message);
      } finally {
        if (!geannuleerd) setLaden(false);
      }
    };
    laad();

    return () => { geannuleerd = true; };
  }, [isAuthenticated, herlaad]);

  // Trek een eigen verzoek in (met bevestiging) en herlaad daarna de lijst.
  const intrekken = async (id) => {
    if (!window.confirm('Dit ruilverzoek intrekken?')) return;
    try {
      await trekRuilverzoekInApi(id);
      setHerlaad((n) => n + 1);
    } catch (err) {
      window.alert(err.message);
    }
  };

  // Weiger een verzoek waar jij de gevraagde collega bent (met bevestiging).
  const weigeren = async (id) => {
    if (!window.confirm('Dit ruilverzoek weigeren?')) return;
    try {
      await weigerRuilverzoekApi(id);
      setHerlaad((n) => n + 1);
    } catch (err) {
      window.alert(err.message);
    }
  };

  // Eén afgehandeld verzoek uit de eigen historie verwijderen (openstaande kunnen hier niet komen).
  const verwijderHistorie = async (id) => {
    try {
      await verwijderRuilHistorieApi(id);
      setHerlaad((n) => n + 1);
    } catch (err) {
      setFout(err.message);
    }
  };

  // De hele eigen historie wissen (met bevestiging).
  const wisHistorie = async () => {
    if (!window.confirm('Alle afgehandelde ruilverzoeken uit je historie verwijderen?')) return;
    try {
      await wisRuilHistorieApi();
      setHerlaad((n) => n + 1);
    } catch (err) {
      setFout(err.message);
    }
  };

  const heeftIets =
    !!data && (data.mijnVerzoeken.length > 0 || (data.historie?.length > 0) || (data.isTeamleider && !previewAlsMedewerker && (data.teBeoordelen.length > 0 || (data.kapotteRuilen || []).length > 0 || (data.inactieveRuilen || []).length > 0)));

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start gap-3 lg:flex-row lg:justify-between">
        <div className="flex items-start gap-2">
          {/* mt-1 vangt de halve regelafstand van text-2xl op (regelhoogte 32px, icoon 24px),
              zodat het icoon optisch op de titelregel staat en niet 4px te hoog. */}
          <ArrowLeftRight className="w-6 h-6 shrink-0 mt-1 text-slate-700" />
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Ruilverzoeken</h1>
            <p className="text-sm text-slate-500 mt-1">Kies een dienst en een collega om een ruil voor te stellen. De teamleider keurt elke ruil goed.</p>
          </div>
        </div>
        {isAuthenticated && (
          <button
            onClick={() => setModalOpen(true)}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-brand-700 hover:bg-brand-800"
          >
            <Plus className="w-4 h-4" /> Nieuw ruilverzoek
          </button>
        )}
      </div>

      {/* 1. Niet ingelogd */}
      {!isAuthenticated && (
        <Melding emoji="🔒"><p>Log in met je Microsoft-account om je ruilverzoeken te zien.</p></Melding>
      )}

      {/* 2. Laden */}
      {isAuthenticated && laden && (
        <Melding emoji="⏳"><p>Ruilverzoeken laden…</p></Melding>
      )}

      {/* 3. Fout */}
      {isAuthenticated && !laden && fout && (
        <Melding emoji="🔒"><p>{fout}</p></Melding>
      )}

      {/* 4. Data */}
      {isAuthenticated && !laden && !fout && data && (
        !heeftIets ? (
          <Melding emoji="📭"><p>Je hebt nog geen ruilverzoeken.</p></Melding>
        ) : (
          <div className="space-y-8">
            {data.isTeamleider && !previewAlsMedewerker && (data.kapotteRuilen || []).length > 0 && (
              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-red-700 uppercase tracking-wide">Staat niet meer goed in het rooster</h2>
                <p className="text-sm text-slate-500 -mt-1">Deze goedgekeurde ruilen staan niet meer op de juiste plek — waarschijnlijk is de maand opnieuw gegenereerd of ontgrendeld. Zet de diensten handmatig recht in de matrix.</p>
                {data.kapotteRuilen.map((v) => (
                  <div key={v.id} className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm">
                    <p><strong>{v.indienerNaam}</strong> ↔ <strong>{v.collegaNaam}</strong></p>
                    <p className="text-slate-600 mt-1">{v.indienerNaam} bood: {v.aangebodenDienst?.datum} — {v.aangebodenDienst?.dienstLabel} {v.aangebodenDienst?.gemeenteLabel}</p>
                    <p className="text-slate-600">{v.collegaNaam} bood terug: {v.tegendienst?.datum} — {v.tegendienst?.dienstLabel} {v.tegendienst?.gemeenteLabel}</p>
                  </div>
                ))}
              </section>
            )}
            {/* Wachtstand, geen probleem: bewust de neutrale grijze stijl, niet de rode hierboven. */}
            {data.isTeamleider && !previewAlsMedewerker && (data.inactieveRuilen || []).length > 0 && (
              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Wacht op vastleggen</h2>
                <p className="text-sm text-slate-500 -mt-1">Deze ruilen zijn goedgekeurd maar staan even stil: zolang de week niet vastligt, wordt de wissel niet toegepast. Er is niets misgegaan — leg de week opnieuw vast en de ruil staat er vanzelf weer in.</p>
                {data.inactieveRuilen.map((v) => (
                  <div key={v.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
                    <p><strong>{v.indienerNaam}</strong> ↔ <strong>{v.collegaNaam}</strong></p>
                    <p className="text-slate-600 mt-1">{v.indienerNaam} bood: {v.aangebodenDienst?.datum} — {v.aangebodenDienst?.dienstLabel} {v.aangebodenDienst?.gemeenteLabel}</p>
                    <p className="text-slate-600">{v.collegaNaam} bood terug: {v.tegendienst?.datum} — {v.tegendienst?.dienstLabel} {v.tegendienst?.gemeenteLabel}</p>
                    {(v.wachtOpWeken || []).length > 0 && (
                      <p className="text-slate-500 mt-2">
                        Wacht op het vastleggen van {v.wachtOpWeken.map((w) => `week ${w.weeknummer}`).join(' en ')}.
                      </p>
                    )}
                  </div>
                ))}
              </section>
            )}
            {data.mijnVerzoeken.length > 0 && (
              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Mijn ruilverzoeken</h2>
                {data.mijnVerzoeken.map((v) => (
                  <VerzoekKaart
                    key={v.id}
                    v={v}
                    mwKey={data.mwKey}
                    onIntrek={intrekken}
                    onWeiger={weigeren}
                    onAccepteer={setTegendienstVerzoek}
                  />
                ))}
              </section>
            )}

            {data.isTeamleider && !previewAlsMedewerker && data.teBeoordelen.length > 0 && (
              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Te beoordelen</h2>
                {data.teBeoordelen.map((v) => (
                  <BeoordeelKaart key={v.id} v={v} onBeoordeeld={() => setHerlaad((n) => n + 1)} />
                ))}
              </section>
            )}

            {data.historie?.length > 0 && (
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setHistorieOpen((o) => !o)}
                    className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 uppercase tracking-wide hover:text-slate-800"
                  >
                    {historieOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    Historie ({data.historie.length})
                  </button>
                  {historieOpen && (
                    <button onClick={wisHistorie} className="text-xs text-slate-400 hover:text-slate-600">
                      Alles wissen
                    </button>
                  )}
                </div>
                {historieOpen && data.historie.map((v) => (
                  <VerzoekKaart
                    key={v.id}
                    v={v}
                    mwKey={data.mwKey}
                    onVerwijder={() => verwijderHistorie(v.id)}
                  />
                ))}
              </section>
            )}
          </div>
        )
      )}

      <NieuwRuilverzoekModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAangemaakt={() => setHerlaad((n) => n + 1)}
        eigenMwKey={data?.mwKey}
        collegas={data?.collegas}
      />

      <TegendienstModal
        open={!!tegendienstVerzoek}
        verzoek={tegendienstVerzoek}
        onClose={() => setTegendienstVerzoek(null)}
        onGekozen={() => setHerlaad((n) => n + 1)}
      />
    </div>
  );
}
